import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PencilSquareIcon,
  PlusIcon,
  ArrowRightIcon,
  BookOpenIcon,
  CreditCardIcon,
  Cog6ToothIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { BoltIcon } from '@heroicons/react/24/solid';
import MobileLayout from '../components/MobileLayout';
import { useAuth } from '../contexts/AuthContext';
import { useDreamscape } from '../contexts/DreamscapeContext';
import { getDailyQuote } from '../utils/dreamscape';
import BrandLogo from '../components/BrandLogo';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { getWriterProfile } from '../features/writerProfile/api';

interface StorySummary {
  id: string;
  title: string;
  format: string;
  status: string;
  wordCount: number;
  updatedAt: string;
  /** The writer's role; stories shared with them are not theirs to count. */
  role?: 'owner' | 'editor' | 'viewer';
}

const formatEmoji: Record<string, string> = {
  novel: '📖',
  short_story: '📝',
  fan_fiction: '✨',
  screenplay: '🎬',
  stage_play: '🎭',
  poetry: '🪶',
};

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const { timeOfDay } = useDreamscape();
  const dailyQuote = getDailyQuote();
  const [stories, setStories] = useState<StorySummary[]>([]);
  const [subscriptionPlan, setSubscriptionPlan] = useState<string>('free');
  const [credits, setCredits] = useState<number | null>(null);
  const [profileIncomplete, setProfileIncomplete] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    const [tokenResponse, storiesResponse] = await Promise.all([
      axios.get('/tokens/balance').catch(() => ({ data: {} })),
      axios.get('/stories').catch(() => ({ data: [] })),
    ]);

    const list: StorySummary[] = Array.isArray(storiesResponse.data) ? storiesResponse.data : [];
    const ts = (s: StorySummary) => {
      const t = new Date(s.updatedAt).getTime();
      return Number.isFinite(t) ? t : 0;
    };
    list.sort((a, b) => ts(b) - ts(a));
    setStories(list);
    setSubscriptionPlan(tokenResponse.data.subscriptionPlan || 'free');
    const balance = Number(tokenResponse.data.currentBalance);
    setCredits(Number.isFinite(balance) ? balance : null);

    getWriterProfile()
      .then((profile) => setProfileIncomplete(!profile?.completedAt))
      .catch(() => setProfileIncomplete(false));
  };

  const ownStories = stories.filter((s) => (s.role ?? 'owner') === 'owner');
  const totalWords = ownStories.reduce((sum, s) => sum + (Number(s.wordCount) || 0), 0);
  const latestStory = stories[0];
  const recentStories = stories.slice(0, 3);

  const getGreeting = () => {
    switch (timeOfDay) {
      case 'morning':
        return t('dashboard.goodMorning', 'Good morning');
      case 'afternoon':
        return t('dashboard.goodAfternoon', 'Good afternoon');
      case 'evening':
        return t('dashboard.goodEvening', 'Good evening');
      case 'night':
        return t('dashboard.goodNight', 'Good night');
    }
  };

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

  return (
    <MobileLayout>
      <div className="flex-1 flex flex-col">
        {/* Header with Greeting */}
        <div className="p-6 pb-4">
          <BrandLogo
            imgClassName="h-7 w-auto mb-5"
            textClassName="block mb-5 text-2xl font-semibold font-cormorant text-dark-text"
          />
          <h1
            className="text-[28px] font-semibold font-cormorant"
            style={{ color: 'var(--ds-text-primary)' }}
          >
            {getGreeting()}, {user?.name || t('dashboard.defaultName', 'there')}
          </h1>
          <p className="font-cormorant italic mt-1" style={{ color: 'var(--ds-text-secondary)' }}>
            {t('dashboard.writerWelcome', 'What will you write today?')}
          </p>
        </div>

        {/* Quick Actions */}
        <div className="px-6 pb-4">
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => navigate(latestStory ? `/stories/${latestStory.id}` : '/stories/new')}
              className="p-4 bg-primary-500 hover:bg-primary-600 rounded-2xl transition-colors text-left"
            >
              <PencilSquareIcon className="w-8 h-8 text-white mb-2" />
              <div className="text-white font-semibold">
                {latestStory
                  ? t('dashboard.continueWriting', 'Continue writing')
                  : t('dashboard.startFirstStory', 'Start your first story')}
              </div>
              {latestStory && (
                <div className="text-white text-sm truncate">{latestStory.title}</div>
              )}
            </button>
            <button
              onClick={() => navigate('/stories/new')}
              className="p-4 rounded-2xl transition-colors text-left ds-card"
              style={{ background: 'var(--ds-bg-card)', border: '1px solid var(--ds-border)' }}
            >
              <PlusIcon className="w-8 h-8 mb-2" style={{ color: 'var(--ds-text-primary)' }} />
              <div className="font-semibold" style={{ color: 'var(--ds-text-primary)' }}>
                {t('dashboard.newStory', 'New story')}
              </div>
            </button>
          </div>
        </div>

        {/* Writer profile nudge (existing accounts that never did onboarding) */}
        {profileIncomplete && (
          <div className="px-6 pb-4">
            <button
              onClick={() => navigate('/onboarding')}
              className="w-full p-4 rounded-2xl text-left ds-card transition-colors hover:opacity-90"
              style={{
                background: 'var(--ds-bg-card)',
                border: '1px solid var(--ds-accent, #a855f7)',
              }}
            >
              <div className="flex items-center gap-3">
                <SparklesIcon className="w-6 h-6 text-primary-500 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold" style={{ color: 'var(--ds-text-primary)' }}>
                    {t('dashboard.profileNudgeTitle', 'Tell the AI how you write')}
                  </div>
                  <div className="text-sm" style={{ color: 'var(--ds-text-secondary)' }}>
                    {t(
                      'dashboard.profileNudgeBody',
                      'Two minutes: your genres, influences and voice.',
                    )}
                  </div>
                </div>
                <ArrowRightIcon
                  className="w-4 h-4 shrink-0"
                  style={{ color: 'var(--ds-text-secondary)' }}
                />
              </div>
            </button>
          </div>
        )}

        {/* Stats Cards */}
        <div className="px-6 pb-4">
          <div
            className="ds-card rounded-2xl p-4"
            style={{ background: 'var(--ds-bg-card)', border: '1px solid var(--ds-border)' }}
          >
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold" style={{ color: 'var(--ds-text-primary)' }}>
                  {ownStories.length}
                </div>
                <div className="text-xs mt-1" style={{ color: 'var(--ds-text-secondary)' }}>
                  {t('dashboard.storiesCount', 'Stories')}
                </div>
              </div>
              <div className="text-center border-l" style={{ borderColor: 'var(--ds-border)' }}>
                <div className="text-2xl font-bold" style={{ color: 'var(--ds-text-primary)' }}>
                  {totalWords.toLocaleString()}
                </div>
                <div className="text-xs mt-1" style={{ color: 'var(--ds-text-secondary)' }}>
                  {t('dashboard.wordsWritten', 'Words written')}
                </div>
              </div>
              <div className="text-center border-l" style={{ borderColor: 'var(--ds-border)' }}>
                <div className="text-2xl font-bold" style={{ color: 'var(--ds-text-primary)' }}>
                  {credits === null ? '—' : credits.toLocaleString()}
                </div>
                <div className="text-xs mt-1" style={{ color: 'var(--ds-text-secondary)' }}>
                  {t('dashboard.credits', 'Credits')}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Subscription Status */}
        <div className="px-6 pb-4">
          <button
            onClick={() => navigate('/subscription')}
            className="w-full p-4 bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl transition-all hover:shadow-lg"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <BoltIcon className="w-6 h-6 text-white" />
                <div className="text-left">
                  <div className="text-white font-semibold">
                    {subscriptionPlan !== 'free'
                      ? t('dashboard.planLabel', '{{plan}} plan', {
                          plan: t(
                            `pricing.${subscriptionPlan}`,
                            subscriptionPlan.charAt(0).toUpperCase() + subscriptionPlan.slice(1),
                          ),
                        })
                      : t('dashboard.explorePlans', 'Explore Plans')}
                  </div>
                  <div className="text-white text-sm">
                    {subscriptionPlan !== 'free'
                      ? t('dashboard.managePlan', 'Manage your subscription')
                      : t('dashboard.findPerfectPlan', 'Find the perfect plan for you')}
                  </div>
                </div>
              </div>
              <ArrowRightIcon className="w-5 h-5 text-white" />
            </div>
          </button>
        </div>

        {/* Recent Stories */}
        {recentStories.length > 0 && (
          <div className="px-6 pb-4">
            <div className="flex items-center justify-between mb-3">
              <h2
                className="text-lg font-semibold font-cormorant"
                style={{ color: 'var(--ds-text-primary)' }}
              >
                {t('dashboard.recentStories', 'Recent stories')}
              </h2>
              <button
                onClick={() => navigate('/stories')}
                className="text-primary-500 text-sm font-medium"
              >
                {t('common.seeAll', 'See all')}
              </button>
            </div>
            <div className="space-y-2">
              {recentStories.map((story) => (
                <button
                  key={story.id}
                  onClick={() => navigate(`/stories/${story.id}`)}
                  className="w-full p-3 ds-card rounded-xl transition-colors hover:opacity-90"
                  style={{ background: 'var(--ds-bg-card)', border: '1px solid var(--ds-border)' }}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary-500/20 rounded-full flex items-center justify-center text-lg">
                      {formatEmoji[story.format] || '📖'}
                    </div>
                    <div className="flex-1 text-left min-w-0">
                      <div
                        className="font-medium truncate"
                        style={{ color: 'var(--ds-text-primary)' }}
                      >
                        {story.title}
                      </div>
                      <div className="text-sm" style={{ color: 'var(--ds-text-secondary)' }}>
                        {(Number(story.wordCount) || 0).toLocaleString()}{' '}
                        {t('stories.words', 'words')} · {formatDate(story.updatedAt)}
                      </div>
                    </div>
                    <ArrowRightIcon
                      className="w-4 h-4 shrink-0"
                      style={{ color: 'var(--ds-text-secondary)' }}
                    />
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Quick Links */}
        <div className="px-6 pb-6">
          <div className="grid grid-cols-3 gap-3">
            <button
              onClick={() => navigate('/stories')}
              className="p-3 rounded-xl transition-colors ds-card"
              style={{ background: 'var(--ds-bg-card)', border: '1px solid var(--ds-border)' }}
            >
              <BookOpenIcon className="w-6 h-6 text-primary-500 mx-auto mb-1" />
              <div className="text-xs" style={{ color: 'var(--ds-text-primary)' }}>
                {t('navigation.stories', 'Stories')}
              </div>
            </button>
            <button
              onClick={() => navigate('/subscription')}
              className="p-3 rounded-xl transition-colors ds-card"
              style={{ background: 'var(--ds-bg-card)', border: '1px solid var(--ds-border)' }}
            >
              <CreditCardIcon className="w-6 h-6 text-yellow-500 mx-auto mb-1" />
              <div className="text-xs" style={{ color: 'var(--ds-text-primary)' }}>
                {t('dashboard.plans', 'Plans')}
              </div>
            </button>
            <button
              onClick={() => navigate('/profile')}
              className="p-3 rounded-xl transition-colors ds-card"
              style={{ background: 'var(--ds-bg-card)', border: '1px solid var(--ds-border)' }}
            >
              <Cog6ToothIcon className="w-6 h-6 text-blue-500 mx-auto mb-1" />
              <div className="text-xs" style={{ color: 'var(--ds-text-primary)' }}>
                {t('navigation.settings', 'Settings')}
              </div>
            </button>
          </div>
        </div>

        {/* Literary Quote */}
        <div className="px-6 pb-8 text-center">
          <p
            className="font-cormorant italic text-base leading-relaxed"
            style={{ color: 'var(--ds-text-secondary)' }}
          >
            &ldquo;{dailyQuote.text}&rdquo;
          </p>
          <p className="text-xs mt-1" style={{ color: 'var(--ds-text-secondary)' }}>
            — {dailyQuote.author}
          </p>
        </div>
      </div>
    </MobileLayout>
  );
};

export default Dashboard;
